import {
  FileUpload,
  Flex,
  Box,
  Image,
  Spinner,
  Button,
  Slider,
  Center,
  HStack,
  Portal,
} from '@chakra-ui/react';
import { Dialog } from '@halo/ui-react';
import {
  CameraIcon,
  RotationIcon,
  MirrorIcon,
  ImgMinIcon,
  ImgMaxIcon,
} from '@halo/ui-chat-react';
import Cropper from 'react-easy-crop';
import { getOrientation } from 'get-orientation/browser';
import { toaster } from '@halo/ui-react';
import { useTranslation } from 'react-i18next';
import { generateThumbnail } from './utils';
import type { PixelCrop } from './canvasUtils';
import { getCroppedImg, getRotatedImage, ORIENTATION_TO_ANGLE } from './canvasUtils';

type FileType = File;

const enum CompressMode {
  Quality = 'Quality',
  Resize = 'Resize',
}

interface UploadAvatarProps {
  value?: string;
  onChange?: (file: File) => Promise<void>;
  children?:
    | React.ReactNode
    | ((value: { loading: boolean; imageUrl: string | undefined }) => React.ReactNode);
  compressMode?: keyof typeof CompressMode;
  compressProps?: Parameters<typeof generateThumbnail>[1];
  className?: string;
  accept?: string;
  disabled?: boolean;
  multiple?: boolean;
  name?: string;
  maxFiles?: number;
  maxSize?: number;
}

function readFile(file: File) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => resolve(reader.result), false);
    reader.readAsDataURL(file);
  });
}

export const UploadAvatar = (props: UploadAvatarProps) => {
  const {
    value,
    onChange,
    children,
    compressMode = CompressMode.Resize,
    compressProps,
    accept = ['image/jpg', 'image/jpeg', 'image/png', 'image/webp', 'image/gif'],
    disabled = false,
    ...restProps
  } = props;
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState(value);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState<any>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<PixelCrop | null>(null);
  // const [croppedImage, setCroppedImage] = useState<any>(null);
  const [fileName, setFileName] = useState('');
  const [applyLoading, setApplyLoading] = useState(false);
  const [isMirror, setIsMirror] = useState(false);
  const [uploadKey, setUploadKey] = useState(0);
  const portalContainerRef = useRef<HTMLDivElement>(
    document.getElementById('app') as HTMLDivElement
  );

  useEffect(() => {
    portalContainerRef.current = document.getElementById('app') as HTMLDivElement;
  }, []);

  const beforeUpload = useCallback(
    async (file: FileType) => {
      setFileName(file.name);
      const allowedTypes = ['image/jpg', 'image/jpeg', 'image/png', 'image/webp', 'image/gif'];
      const isOk = allowedTypes.includes(file.type);
      if (!isOk) {
        toaster.error({
          title: t('tips.fileSuffixError'),
        });
        return false;
      }

      const isLt10MB = file.size / 1024 / 1024 <= 10;
      if (!isLt10MB) {
        toaster.error({
          title: t('tips.maxImageSize', { size: 10 }),
        });
        return false;
      }

      return true;
    },
    [t]
  );

  const blobToFile = useCallback((blob: Blob, name: string) => {
    return new File([blob], name, { type: blob.type });
  }, []);

  const showCroppedImage = useCallback(async () => {
    try {
      const croppedImage = await getCroppedImg(
        imageSrc,
        croppedAreaPixels as PixelCrop,
        rotation,
        { horizontal: isMirror, vertical: false }
      );
      if (croppedImage) {
        setApplyLoading(true);
        await onChange?.(blobToFile(croppedImage, fileName));
        setApplyLoading(false);
        setCropModalOpen(false);
      }
      // setCroppedImage(URL.createObjectURL(croppedImage!));
    } catch (e) {
      console.log(e);
    }
  }, [imageSrc, croppedAreaPixels, rotation, isMirror, onChange, blobToFile, fileName]);

  const onCropComplete = useCallback((_: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileChange = useCallback(
    async (details: { acceptedFiles: FileType[] }) => {
      const files = details.acceptedFiles;
      if (files.length > 0) {
        const file = files[0];

        if (!beforeUpload(file)) {
          return;
        }

        let imageDataUrl = await readFile(file);
        try {
          // apply rotation if needed
          const orientation: any = await getOrientation(file);
          const rotation = ORIENTATION_TO_ANGLE[orientation as keyof typeof ORIENTATION_TO_ANGLE];
          if (rotation) {
            imageDataUrl = await getRotatedImage(imageDataUrl, rotation);
          }
        } catch {
          console.warn('failed to detect the orientation');
        }
        setImageSrc(imageDataUrl);
        setCropModalOpen(true);

        setLoading(true);
        // let file_ = file;

        if (compressMode === CompressMode.Resize) {
          generateThumbnail(file, {
            genFile: true,
            ...compressProps,
          }).finally(() => {
            setLoading(false);
          });
          // .then(({ file: newFile }) => {
          //   if (newFile) {
          //     file_ = newFile;
          //   }
          // })
        }

        // console.log('file', file_);
      }
    },
    [beforeUpload, compressMode, compressProps]
  );

  const children_ = useMemo(() => {
    return children ? (
      typeof children === 'function' ? (
        children({ loading, imageUrl })
      ) : (
        children
      )
    ) : (
      <Flex
        pos="relative"
        align="center"
        justifyContent="center"
        w="84px"
        h="84px"
        overflow="hidden"
      >
        {loading ? (
          <Spinner />
        ) : imageUrl ? (
          <Box w="full" h="full">
            <Image src={imageUrl} alt="avatar" />
            <Box
              pos="absolute"
              display="none"
              inset={0}
              alignItems="center"
              justifyContent="center"
              bg="rgb(0 0 0 / 30%)"
            >
              <CameraIcon width="24px" height="24px" />
            </Box>
          </Box>
        ) : (
          <CameraIcon width="24px" height="24px" />
        )}
      </Flex>
    );
  }, [children, loading, imageUrl]);

  useEffect(() => {
    setImageUrl(value);
  }, [value]);

  useEffect(() => {
    if (!cropModalOpen) {
      setUploadKey((k) => k + 1);
      setImageSrc(null);
      setIsMirror(false);
      setRotation(0);
      setZoom(1);
      setCroppedAreaPixels(null as any);
    }
  }, [cropModalOpen]);

  return (
    <>
      <FileUpload.Root
        key={uploadKey}
        disabled={disabled}
        onFileChange={handleFileChange}
        accept={accept}
        {...restProps}
      >
        <FileUpload.HiddenInput />
        <FileUpload.Trigger asChild cursor={disabled ? 'inherit' : 'pointer'}>
          {children_}
        </FileUpload.Trigger>
      </FileUpload.Root>
      <Dialog.Root
        placement="center"
        open={cropModalOpen}
        onOpenChange={(details) => {
          setCropModalOpen(details.open);
        }}
      >
        <Dialog.Trigger />
        <Portal container={portalContainerRef}>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content bg="bg.subtle">
              <Dialog.Header pt="6.5" px="5" pb="2.5">
                <Dialog.Title fontWeight="semibold">{t('chat.editAvatar')}</Dialog.Title>
              </Dialog.Header>
              <Dialog.Body px="5">
                {imageSrc && (
                  <>
                    <Box pos="relative" width="100%" height="300px">
                      <Cropper
                        cropShape="round"
                        image={imageSrc}
                        crop={crop}
                        rotation={rotation}
                        zoom={zoom}
                        // cropSize={{
                        //   width: 178,
                        //   height: 178,
                        // }}
                        aspect={1 / 1}
                        showGrid={false}
                        onCropChange={setCrop}
                        onRotationChange={setRotation}
                        onCropComplete={onCropComplete}
                        onZoomChange={setZoom}
                        style={{
                          containerStyle: {
                            borderRadius: '4px',
                            background: '#1A1A1A',
                            transform: isMirror ? 'scaleX(-1)' : 'none',
                          },
                        }}
                      />
                    </Box>
                    <HStack justifyContent="space-between" alignItems="center" mt="4">
                      <MirrorIcon cursor="pointer" onClick={() => setIsMirror(!isMirror)} />

                      <Center width="120px" gap="1">
                        {/* <Box w="120px" fontSize="xs">
                        Zoom
                      </Box> */}
                        <ImgMinIcon />
                        <Slider.Root
                          size="sm"
                          width="70%"
                          value={[zoom]}
                          onValueChange={({ value }: { value: number[] }) => {
                            setZoom(Number(value[0]));
                          }}
                          min={1}
                          max={3}
                          step={0.1}
                        >
                          <Slider.Control>
                            <Slider.Track>
                              <Slider.Range />
                            </Slider.Track>
                            <Slider.Thumbs />
                          </Slider.Control>
                        </Slider.Root>
                        <ImgMaxIcon />
                      </Center>
                      <RotationIcon
                        cursor="pointer"
                        onClick={() => setRotation(rotation === 270 ? 0 : rotation + 90)}
                      />
                    </HStack>
                    {/* <img src={croppedImage} alt="avatar" width={100} height={100} /> */}

                    {/* <Center mt="10px">
                    <Box w="120px" fontSize="xs">
                      Rotation
                    </Box>
                    <Slider.Root
                      size="sm"
                      width="70%"
                      value={[rotation]}
                      onValueChange={({ value }: { value: number[] }) => {
                        setRotation(Number(value[0]));
                      }}
                      min={0}
                      max={360}
                      step={1}
                    >
                      <Slider.Control>
                        <Slider.Track>
                          <Slider.Range />
                        </Slider.Track>
                        <Slider.Thumbs />
                      </Slider.Control>
                    </Slider.Root>
                  </Center> */}
                  </>
                )}
              </Dialog.Body>
              <Dialog.Footer>
                <Dialog.ActionTrigger asChild>
                  <Button
                    variant="subtle"
                    color="fg"
                    bg="bg.subtle"
                    rounded="xl"
                    minW="96px"
                    fontWeight="medium"
                  >
                    {t('buttons.cancel')}
                  </Button>
                </Dialog.ActionTrigger>
                <Button
                  rounded="xl"
                  minW="96px"
                  loading={applyLoading}
                  onClick={showCroppedImage}
                  fontWeight="medium"
                >
                  {t('buttons.apply')}
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  );
};

export default UploadAvatar;
